import { Injectable } from '@angular/core';
import { StoryDataHelpers } from './story-helpers';
import { IStory, IStoriesPageResponse, IStoryResponse, IStoriesPageRequest, EBundleIncludeType } from '../../classes/def/core/story';
import { IGenericResponse } from '../../classes/def/requests/general';
import { GenericDataService } from '../general/data/generic';
import { InventoryDataService } from './inventory';
import { LocationUtilsWizard } from '../location/location-utils-wizard';
import { IBookingInfoContent, IBookingInterceptorResult } from 'src/app/classes/def/core/booking';
import { ILatLng } from 'src/app/classes/def/map/coords';
import { TimeUtils } from 'src/app/classes/general/time';
import { IDBModelLanguage } from 'src/app/classes/def/nav-params/story';

@Injectable({
    providedIn: 'root'
})
export class MarketplaceDataService {

    constructor(
        public generic: GenericDataService,
        public inventory: InventoryDataService,
        public locationUtilsWizard: LocationUtilsWizard
    ) {
        console.log("marketplace data service created");
    }


    /**
     * get all stories from the backend that are on the same page with the story having the specified name
     * the stories don't contain details
     * they are added to the local object to be shown in the story list
     */
    async searchStoryByName(coords: ILatLng, categoryCode: number, providerId: number, cityId: number, name: string): Promise<IStoriesPageResponse> {
        let promise: Promise<IStoriesPageResponse> = new Promise((resolve, reject) => {
            let req: IStoriesPageRequest = {
                lat: coords ? coords.lat : null,
                lng: coords ? coords.lng : null,
                categoryCode: categoryCode,
                providerId: providerId,
                cityId: cityId,
                name: name,
                includeBundles: EBundleIncludeType.includeBundles
            };
            this.listStoriesCore(req).then((data: IStoriesPageResponse) => {
                resolve(data);
            }).catch((err: Error) => {
                reject(err);
            });
        });
        return promise;
    }

    /**
     * get all stories from the backend
     * the stories don't contain details
     * they are added to the local object to be shown in the story list
     */
    listStories(coords: ILatLng, categoryCode: number, providerId: number, cityId: number, releaseFilter: number[], orderBy: number, sort: number, min: boolean, page: number, pageSize: number): Promise<IStoriesPageResponse> {
        let promise: Promise<IStoriesPageResponse> = new Promise((resolve, reject) => {
            let req: IStoriesPageRequest = {
                lat: coords ? coords.lat : null,
                lng: coords ? coords.lng : null,
                categoryCode: categoryCode,
                providerId: providerId,
                cityId: cityId,
                min: min,
                page: page,
                releaseFilter: releaseFilter,
                orderBy: orderBy,
                sort: sort,
                includeBundles: EBundleIncludeType.includeBundles,
                pageSize: pageSize
            };
            this.listStoriesCore(req).then((data: IStoriesPageResponse) => {
                resolve(data);
            }).catch((err: Error) => {
                reject(err);
            });
        });
        return promise;
    }

    listStoriesCore(req: IStoriesPageRequest) {
        let promise = new Promise((resolve, reject) => {
            let route: string = "/marketplace/list-stories";
            console.log(req);
            if (req.page == null && req.name != null) {
                route = "/marketplace/search-story-by-name";
            }
            this.generic.genericPostNoAuth(route, req).then((response: IGenericResponse) => {
                if (response.status) {
                    let stories: IStory[] = [];
                    let responseData: IStoriesPageResponse = response.data;
                    for (let i = 0; i < responseData.stories.length; i++) {
                        let story: IStory = responseData.stories[i];
                        story = StoryDataHelpers._formatStoryFromBackend(story);
                        stories.push(story);
                    }
                    console.log(stories);
                    let responseDataFormat: IStoriesPageResponse = {
                        stories: stories,
                        page: responseData.page,
                        pages: responseData.pages
                    };

                    resolve(responseDataFormat);
                } else {
                    reject(new Error(response.message));
                }
            }).catch((err: Error) => {
                reject(err);
            });
        });
        return promise;
    }


    /**
     * get a story from server
     * save it into the local object (buffer)
     * update if existing story with id
     * @param storyId 
     */
    getStory(storyId: number, coords: ILatLng, token: string, fromBundle: boolean): Promise<IStoryResponse> {
        let promise: Promise<IStoryResponse> = new Promise((resolve, reject) => {
            let story: IStory;
            let req = {
                storyId: storyId,
                lat: coords ? coords.lat : null,
                lng: coords ? coords.lng : null,
                token: token
            };

            this.generic.genericPostNoAuth(fromBundle ? "/marketplace/get-story-preview-from-bundle" : "/marketplace/get-story-preview", req).then((response: IGenericResponse) => {
                if (response.status) {
                    let data: IStoryResponse = response.data;
                    if (!data.locked) {
                        story = StoryDataHelpers._formatStoryFromBackend(data.story);
                    }
                    resolve(data);
                } else {
                    reject(new Error(response.message));
                }
            }).catch((err: Error) => {
                reject(err);
            });
        });
        return promise;
    }

    getAvailableTranslations(storyId: number): Promise<IDBModelLanguage[]> {
        let req = {
            storyId: storyId
        };
        return this.generic.genericPostNoAuthWData("/marketplace/get-story-translations-list", req);
    }

    loadStoryTranslation(storyId: number, languageId: number): Promise<IStory> {
        let req = {
            storyId: storyId,
            languageId: languageId
        };
        return this.generic.genericPostNoAuthWData("/marketplace/get-story-translation", req);
    }


    requestEmbedTutorial(path: string) {
        return this.generic.genericGetNoAuth("/marketplace/get-embed-tutorial", {
            path: path
        });
    }

    getBookingMessage(bookingReference: string, bookingProvider: number, bookingCustomerName: string, bookingId: number): Promise<IBookingInfoContent> {
        return new Promise(async (resolve, reject) => {
            try {
                let bic: IBookingInfoContent = await this.generic.genericGetNoAuthWData("/booking/interceptor/get-booking-details-for-booking-reference", {
                    bookingId: bookingId,
                    bookingReference: bookingReference,
                    bookingCustomerName: bookingCustomerName,
                    bookingProvider: bookingProvider
                });
                let description: string = "";
                description += "<p>Booking Reference: " + bic.entry.bookingReference + "</p>";
                description += "<p>Booked on: " + TimeUtils.formatDateDisp(bic.entry.timestamp as any) + "</p>";
                description += "<p>Scheduled on: " + TimeUtils.formatDateDisp(bic.entry.bookingTimestamp as any) + "</p>";
                description += "<p>PAX: " + bic.entry.bookingAmount + "</p>";
                if (bic.entry.otherBookingReferences != null && bic.entry.otherBookingReferences.length > 0) {
                    description += "<p>Other booking references found on your account: " + bic.entry.otherBookingReferences.join(", ") + "</p>";
                }
                description += "<p>----------</p>";
                description += "<p>INTRO MESSAGE:</p>";
                description += bic.instructions;
                bic.description = description;
                resolve(bic);
            } catch (err) {
                reject(err);
            }
        });
    }

    registerInternalBooking(storyId: number, customerEmail: string, customerName: string, customerPhone: string, bookingDate: string, bookingAmount: number): Promise<IBookingInterceptorResult> {
        return this.generic.genericPostNoAuthWData("/booking/interceptor/register-internal-booking", {
            storyId: storyId,
            customerEmail: customerEmail,
            customerName: customerName,
            customerPhone: customerPhone,
            bookingDate: bookingDate,
            bookingAmount: bookingAmount,
            tester: true
        });
    }

    loadSettings() {
        return this.generic.genericGetNoAuthWData("/marketplace/load-settings", {

        });
    }
}
